import type { CSSObject } from '@mui/material';
import { styled, ButtonBase } from '@mui/material';
import { forwardRef } from 'react';

import { ChkIconLoader } from '../icons';

import { CHK_BUTTON_VARIANT } from './ChkButton.constants';
import type { ChkButtonProps, ChkButtonVariant } from './ChkButton.types';

/**
 * @deprecated Use `UiButton` from `@ui/atoms-button` instead.
 * For icons, use `UiIconLayout` component: `<UiButton><UiIconLayout startIcon={...} endIcon={...}>...</UiIconLayout></UiButton>`
 * For loading states, use `UiButtonWithLoading` from `@ui/molecules/button-with-loading`.
 *
 * Migration guide:
 * - Basic usage: Replace `ChkButton` with `UiButton` (same variants available)
 * - With icons: Use `UiButton` + `UiIconLayout` component
 * - With loading: Use `UiButtonWithLoading` component
 */

export const ChkButton = forwardRef<HTMLButtonElement | null, ChkButtonProps>(
  (
    {
      children,
      endIcon,
      startIcon,
      isLoading,
      loaderProps,
      disabled,
      ...rest
    }: ChkButtonProps,
    ref
  ) => (
    <StyledButton
      aria-label={isLoading ? 'Loading' : undefined}
      aria-disabled={isLoading && disabled ? true : undefined}
      disabled={isLoading && disabled ? false : disabled}
      disableTouchRipple
      {...rest}
      ref={ref}
    >
      {isLoading && (
        <LoadingWrapper>
          <ChkIconLoader {...loaderProps} />
        </LoadingWrapper>
      )}
      <ContentWrapper $isLoading={isLoading}>
        {startIcon && <StartIconRoot aria-hidden>{startIcon}</StartIconRoot>}
        {children}
        {endIcon && <EndIconRoot aria-hidden>{endIcon}</EndIconRoot>}
      </ContentWrapper>
    </StyledButton>
  )
);

const LoadingWrapper = styled('span')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ContentWrapper = styled('span')<{ $isLoading?: boolean }>(
  ({ $isLoading }) => ({
    display: 'contents',
    color: $isLoading ? 'transparent' : 'inherit',
    '& span': {
      visibility: $isLoading ? 'hidden' : 'inherit',
    },
  })
);

const StyledButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant: ChkButtonVariant }>(({ theme, variant }) => {
  const commonLayoutStyles: CSSObject = {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4, 6),
  };
  const commonBorderStyles: CSSObject = {
    border: theme.borders.borderWidth1,
    borderRadius: theme.borders.borderRadius1,
  };
  const commonTypographyStyles: CSSObject = {
    ...theme.typography.body1,
    fontWeight: theme.typography.fontWeightMedium,
  };
  const commonStyles: CSSObject = {
    cursor: 'pointer',
    transition: `
      background-color 150ms ease-in,
      border-color 150ms ease-in,
      color 150ms ease-in
    `,
    '&[aria-disabled]': {
      pointerEvents: 'none',
    },
  };
  const linkStyles: CSSObject = {
    borderColor: 'transparent',
    color: theme.tokens.textLinksTextButtonsDefault,
    '&:hover': {
      color: theme.tokens.textLinksTextButtonsHover,
    },
    '&:focus': {
      color: theme.tokens.textLinksTextButtonsPressed,
    },
    '&:disabled,&[aria-disabled]': {
      color: theme.tokens.textLinksTextButtonsDisabled,
    },
  };
  const textStyles: CSSObject = {
    padding: 0,
    borderColor: 'transparent',
    color: theme.tokens.buttonOutlinedValueDefault,
    '&:focus-visible': {
      borderColor: theme.tokens.optionCardsBorderActive,
    },
    '&:disabled,&[aria-disabled]': {
      color: theme.tokens.textLinksTextButtonsDisabled,
    },
  };
  const accessibilitySizeRequirements: CSSObject = {
    minWidth: theme.spacing(10),
    minHeight: theme.spacing(10),
  };
  const ContainedOutlinedCommonStyles: CSSObject = {
    ...commonLayoutStyles,
    ...commonBorderStyles,
    ...commonTypographyStyles,
    ...commonStyles,
  };

  switch (variant) {
    case CHK_BUTTON_VARIANT.CONTAINED:
      return {
        ...ContainedOutlinedCommonStyles,
        ...accessibilitySizeRequirements,
        color: theme.tokens.buttonContainedValueDefault,
        backgroundColor: theme.tokens.buttonContainedBackgroundDefault,
        borderColor: theme.tokens.buttonContainedBackgroundDefault,
        '&:hover': {
          backgroundColor: theme.tokens.buttonContainedBackgroundHover,
          borderColor: theme.tokens.buttonContainedBackgroundHover,
        },
        '&:focus': {
          backgroundColor: theme.tokens.buttonContainedBackgroundPressed,
          borderColor: theme.tokens.buttonContainedBackgroundPressed,
        },
        '&:disabled,&[aria-disabled]': {
          backgroundColor: theme.tokens.buttonContainedBackgroundDisabled,
          color: theme.tokens.buttonContainedValueDisabled,
          borderColor: theme.tokens.buttonContainedBackgroundDisabled,
        },
      };

    case CHK_BUTTON_VARIANT.OUTLINED:
      return {
        ...ContainedOutlinedCommonStyles,
        ...accessibilitySizeRequirements,
        backgroundColor: theme.tokens.buttonOutlinedBackgroundDefault,
        color: theme.tokens.buttonOutlinedValueDefault,
        borderColor: theme.tokens.buttonOutlinedBorderDefault,
        '&:hover': {
          backgroundColor: theme.tokens.buttonOutlinedBackgroundHover,
          color: theme.tokens.buttonOutlinedValueDefault,
          borderColor: theme.tokens.buttonOutlinedBorderHover,
        },
        '&:focus': {
          backgroundColor: theme.tokens.buttonOutlinedBackgroundHover,
          color: theme.tokens.buttonOutlinedValueDefault,
          borderColor: theme.tokens.buttonOutlinedBorderDefault,
        },
        '&:disabled,&[aria-disabled]': {
          backgroundColor: theme.tokens.buttonOutlinedBackgroundDisabled,
          color: theme.tokens.buttonOutlinedValueDisabled,
          borderColor: theme.tokens.buttonOutlinedBorderDisabled,
        },
      };

    case CHK_BUTTON_VARIANT.LINK:
      return {
        ...ContainedOutlinedCommonStyles,
        ...accessibilitySizeRequirements,
        ...linkStyles,
      };

    case CHK_BUTTON_VARIANT.INLINE_LINK:
      return {
        ...ContainedOutlinedCommonStyles,
        ...linkStyles,
      };

    case CHK_BUTTON_VARIANT.TEXT:
      return {
        ...ContainedOutlinedCommonStyles,
        ...accessibilitySizeRequirements,
        ...textStyles,
      };

    case CHK_BUTTON_VARIANT.INLINE_TEXT:
      return {
        ...ContainedOutlinedCommonStyles,
        ...textStyles,
      };

    case CHK_BUTTON_VARIANT.TERTIARY:
      return {
        ...ContainedOutlinedCommonStyles,
        ...accessibilitySizeRequirements,
        padding: theme.spacing(3, 6),
        border: theme.borders.borderWidth0,
        backgroundColor: theme.tokens.buttonSmallBackgroundDefault,
        ...theme.typography.body2,
        fontWeight: theme.typography.fontWeightRegular,
        color: theme.tokens.buttonSmallValueDefault,
        '&:hover, &:focus-visible': {
          backgroundColor: theme.tokens.buttonSmallBackgroundHover,
        },
        '&:active ': {
          backgroundColor: theme.tokens.buttonSmallBackgroundPressed,
        },
        '&:disabled,&[aria-disabled]': {
          backgroundColor: theme.tokens.buttonContainedBackgroundDisabled,
          color: theme.tokens.buttonContainedValueDisabled,
        },
      };
  }
});

const StartIconRoot = styled('span')(({ theme }) => ({
  display: 'inherit',
  marginRight: theme.spacing(2),
}));

const EndIconRoot = styled('span')(({ theme }) => ({
  display: 'inherit',
  marginLeft: theme.spacing(3),
}));
